/* eslint-disable import/prefer-default-export */

const DEFAULT_RETRY_DELAY = 1000;

export const asyncRetryWithExponentialBackOff = async (fn, retryOptions) => {
  try {
    return await fn();
  } catch (error) {
    if (retryOptions.errorMatch(error) && retryOptions.remainingTries > 0) {
      const newRetryOptions = {
        ...retryOptions,
        remainingTries: retryOptions.remainingTries - 1,
        retryDelay: (retryOptions.retryDelay ?? DEFAULT_RETRY_DELAY) * 2 // exponential backoff
      };
      await new Promise(resolve => {
        setTimeout(resolve, newRetryOptions.retryDelay);
      });
      return asyncRetryWithExponentialBackOff(fn, newRetryOptions);
    }
    throw error;
  }
};

export const asyncRetryIfGatewayError = async (fn, retryOptions = {}) => {
  const defaultOptions = {
    errorMatch: error => /502 Bad|504 Gateway/.test(error.message),
    remainingTries: 3,
    retryDelay: 2_000 // initial delay of 2 seconds
  };
  return asyncRetryWithExponentialBackOff(fn, { ...defaultOptions, ...retryOptions });
};

export const isTransientError = error => {
  const status = error?.response?.status || error?.status;
  if (status && [502, 503, 504].includes(status)) return true;
  if (/Network Error|timeout/i.test(error?.message)) return true;
  return false;
};

export const asyncRetryIfTransientError = (fn, retryOptions = {}) => {
  const defaultOptions = {
    errorMatch: isTransientError,
    remainingTries: 2,
    retryDelay: 1_000
  };
  return asyncRetryWithExponentialBackOff(fn, { ...defaultOptions, ...retryOptions });
};

const FETCH_RETRY_OPTIONS = {
  maxAttempts: process.env.NODE_ENV === "test" ? 1 : 3,
  retryDelay: 500
};

export const fetchWithRetry = async (url, params, retryOptions = FETCH_RETRY_OPTIONS) =>
  asyncRetryWithExponentialBackOff(() => fetch(url, params), {
    errorMatch: () => true,
    remainingTries: retryOptions.maxAttempts - 1,
    retryDelay: retryOptions.retryDelay
  });
